import csvtojson from 'csvtojson';
import { loadFromUrl } from './csv/loadFromUrl';

interface CSVColumn<T> {
  header: string;
  accessor: (item: T) => string | number;
}

export const downloadCSV = <T extends object>(data: T[], columns: CSVColumn<T>[], filename: string) => {
  // Create CSV header row
  const headerRow = columns.map(col => `"${col.header}"`).join(',');
  
  // Create CSV data rows
  const dataRows = data.map(item => 
    columns
      .map(col => {
        const value = col.accessor(item);
        return typeof value === 'string' ? `"${value}"` : value;
      })
      .join(',')
  );
  
  // Combine header and data rows
  const csvContent = [headerRow, ...dataRows].join('\n');
  
  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const parseCSV = async (csvString: string): Promise<any[]> => {
  try {
    return await csvtojson().fromString(csvString);
  } catch (error) {
    console.error('Error parsing CSV:', error);
    throw new Error('Failed to parse CSV file');
  }
};

export const loadAndSaveTable = async <T>(
  csvUrl: string,
  mapper: {
    fromCSV: (row: any) => T;
  },
  saveFunction: (item: T) => void
) => {
  try {
    const csvData = await loadFromUrl(csvUrl);
    const parsed = await parseCSV(csvData);
    const imported = parsed.map(row => mapper.fromCSV(row));

    const validImports = imported.filter(item => {
      const isValid = item && typeof item === "object";
      if (!isValid) {
        console.warn("Skipping invalid import item:", item);
      }
      return isValid;
    });

    validImports.forEach(item => saveFunction(item));
  } catch (error) {
    console.error(`Failed to load data from ${csvUrl}:`, error);
    throw new Error("Failed to load and save table data. Please check the URL and mapper.");
  }
};
